import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AiChatDropHint, cn, getUserAvatarUrl, useFileDrop } from '@bliks-io/ui';
import { ChatErrorBanner } from '@/components/chat';
import { useSubmitAiFeedback } from '@/api/mutations/useSubmitAiFeedback';
import { BliksyChatHeader } from './BliksyChatHeader';
import { BliksyChatMessages } from './BliksyChatMessages';
import { BliksyChatInput } from './BliksyChatInput';
import { useBliksyChat } from './useBliksyChat';
import { useProfile } from '@/api/queries/useProfile';

/** Width bounds and edge margin (px) for the resizable overlay panel. Default
 *  width matches the prior `w-96`. The margin is the inset from the canvas edge;
 *  the parent adds it to the width to know how much the panel covers. */
export const BLIKSY_MIN_WIDTH = 320;
export const BLIKSY_MAX_WIDTH = 600;
export const BLIKSY_DEFAULT_WIDTH = 384;
export const BLIKSY_PANEL_MARGIN = 5;

/** Clamp a candidate width to the panel's allowed range. */
export const clampBliksyWidth = (width: number): number =>
  Math.min(BLIKSY_MAX_WIDTH, Math.max(BLIKSY_MIN_WIDTH, width));

// Width step (px) for keyboard resizing of the handle.
const RESIZE_KEY_STEP = 24;

interface BliksyChatPanelProps {
  sessionId: string;
  /** Org slug for entity-source chip links (threaded from the typed route). */
  slug: string;
  isOpen: boolean;
  onClose: () => void;
  /** Current panel width in px (controlled by the parent so the canvas can offset). */
  width: number;
  /** Live width updates while dragging — the parent reflows the canvas to match. */
  onWidthChange: (width: number) => void;
  /** Final width once a drag or keyboard adjust settles — the parent persists it. */
  onWidthCommit: (width: number) => void;
  className?: string;
}

/**
 * Inner chat content - only mounted when panel is open.
 * This ensures history loads fresh and scroll works correctly.
 */
function BliksyChatContent({
  sessionId,
  slug,
  onClose
}: {
  sessionId: string;
  slug: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const {
    messages,
    inputValue,
    isLoading,
    isHistoryLoading,
    error,
    attachments,
    setInputValue,
    handleSend,
    retry
  } = useBliksyChat({ sessionId });

  const { data: profile } = useProfile();
  const userAvatar = getUserAvatarUrl(profile, 'thumbnail');

  const handleFeedback = useSubmitAiFeedback(sessionId);

  // Files may be dropped anywhere on the panel, not only onto the composer —
  // the transcript is the biggest target and the one the pointer is over. It
  // closes on the same gates as the attach button: a drop mid-answer would
  // attach to a turn that has already been sent.
  //
  // CLOSED, never absent: the panel stays a drop target while it is busy so the
  // drop is swallowed. Handing the event back to the browser would open the
  // file in the tab and take the user's draft with it.
  const { isDraggingOver, dropHandlers } = useFileDrop(
    attachments.addFiles,
    !isHistoryLoading && !isLoading
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col" {...dropHandlers}>
      <BliksyChatHeader onClose={onClose} />

      <BliksyChatMessages
        messages={messages}
        slug={slug}
        isLoading={isLoading}
        isHistoryLoading={isHistoryLoading}
        userAvatar={userAvatar}
        onFeedback={handleFeedback}
      />

      <ChatErrorBanner error={error} onRetry={retry} />

      <BliksyChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={handleSend}
        disabled={isLoading}
        attachments={attachments}
        isHistoryLoading={isHistoryLoading}
      />

      <p className="px-4 pb-2 text-center text-xs text-muted-foreground/60">
        {t('bliksy.disclaimer')}
      </p>

      {isDraggingOver && <AiChatDropHint label={t('chat.attachment.dropHint')} />}
    </div>
  );
}

/**
 * Main chat panel component that combines header, messages, and input.
 * Slides in from the bottom-left and overlays the canvas; the user can drag the
 * right edge to resize it. Width is controlled by the parent so the canvas can
 * keep its content centered in the region the panel leaves free.
 *
 * Content only mounts when open to ensure proper scroll behavior.
 */
export function BliksyChatPanel({
  sessionId,
  slug,
  isOpen,
  onClose,
  width,
  onWidthChange,
  onWidthCommit,
  className
}: BliksyChatPanelProps) {
  const { t } = useTranslation();

  // Drag origin captured on pointer-down so moves resolve to an absolute width.
  // `lastWidth` tracks the live value so the commit doesn't depend on prop sync.
  const dragRef = useRef<{ startX: number; startWidth: number; lastWidth: number } | null>(null);

  const handleResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: width, lastWidth: width };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleResizeMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const next = clampBliksyWidth(drag.startWidth + (e.clientX - drag.startX));
    drag.lastWidth = next;
    onWidthChange(next);
  };

  // Fires on pointer-up and on cancel/interruption (implicit capture release),
  // so the drag always settles and the width gets persisted exactly once.
  const handleResizeEnd = () => {
    const drag = dragRef.current;
    if (!drag) return;
    dragRef.current = null;
    onWidthCommit(drag.lastWidth);
  };

  const handleResizeKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = clampBliksyWidth(width + RESIZE_KEY_STEP);
    else if (e.key === 'ArrowLeft') next = clampBliksyWidth(width - RESIZE_KEY_STEP);
    else if (e.key === 'Home') next = BLIKSY_MIN_WIDTH;
    else if (e.key === 'End') next = BLIKSY_MAX_WIDTH;
    if (next === null) return;
    e.preventDefault();
    onWidthChange(next);
    onWidthCommit(next);
  };

  return (
    <div
      style={{ width, margin: BLIKSY_PANEL_MARGIN }}
      className={cn(
        'absolute left-0 top-0 bottom-0 z-(--z-sticky)',
        'rounded-[15px] overflow-hidden',
        'flex flex-col bg-background shadow-xl',
        'border border-border',
        // Expand from bottom-left (where the button is). Only transform/opacity
        // animate so dragging the resize handle changes width instantly.
        'origin-bottom-left transition-[transform,opacity] duration-300 ease-out',
        isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none',
        className
      )}
    >
      {isOpen && <BliksyChatContent sessionId={sessionId} slug={slug} onClose={onClose} />}

      {/* Drag (or arrow/Home/End keys when focused) the right edge to resize. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('bliksy.resizeHandle')}
        aria-valuenow={width}
        aria-valuemin={BLIKSY_MIN_WIDTH}
        aria-valuemax={BLIKSY_MAX_WIDTH}
        tabIndex={0}
        onPointerDown={handleResizeStart}
        onPointerMove={handleResizeMove}
        onLostPointerCapture={handleResizeEnd}
        onKeyDown={handleResizeKeyDown}
        className="absolute right-0 top-0 bottom-0 z-10 w-1.5 cursor-col-resize touch-none bg-transparent transition-colors hover:bg-success/40 focus-visible:bg-success/40 focus-visible:outline-none"
      />
    </div>
  );
}
